<template>
  <div>
    <div
      class="filter-bar"
      style="margin-top:10px"
    >
      <div class="filter-bar__option">
        <DatePicker
          id="datepicker"
          v-model="filter.date"
          type="date"
          :disabled="isLoading"
          format="dd-MM-yyyy"
          value-format="yyyy-MM-dd"
          :picker-options="pickerOptions"
          :placeholder="trans('core.date') + '...'"
        />
      </div>
      <span
        class="more-options"
        @click="showMoreFilterOptions = !showMoreFilterOptions"
      >
        {{ trans('subscription.more_filteroptions') }}
        <span
          :class="{ 'fa fa-caret-down': !showMoreFilterOptions, 'fa fa-caret-up': showMoreFilterOptions }"
          class="collapse-caret"
          style="margin-left: 5px;"
        />
      </span>
    </div>

    <transition name="slide-fade">
      <div
        v-if="showMoreFilterOptions"
        class="filter-bar"
        style="margin-bottom:10px"
      >
        <div class="filter-bar__option">
          <TimeSelect
            id="timeSelect"
            v-model="filter.time"
            type="time"
            :disabled="isLoading"
            format="HH:mm"
            :picker-options="{
              start: '07:00',
              step: '00:15',
              end: '22:00',
              format : 'HH::mm'
            }"
            value-format="HH:mm"
            :placeholder="trans('subscription.lesson_starttime') + '...'"
          />
        </div>
        <div class="filter-bar__option">
          <select
            v-model="filter.classroom"
            :disabled="isLoading"
            class="form-control"
          >
            <option
              v-for="classroom in classroomFilterList"
              :key="classroom.id"
              :value="classroom.id"
            >
              {{ classroom.name }}
            </option>
          </select>
        </div>
        <div class="filter-bar__option">
          <select
            v-model="filter.eventCourse"
            :disabled="isLoading"
            class="form-control"
          >
            <option
              v-for="course in courseFilterList"
              :key="course.id"
              :value="course.id"
            >
              {{ course.name }}
            </option>
          </select>
        </div>
      </div>
    </transition>

    <div
      v-if="filter.date != null || filter.classroom != null"
    >
      <EmployeeDayCalendarPerClassroom
        :is-loading="isLoading"
        :subscribables="subscribables"
        :classroom-selected="filter.classroom != null"
        :store-route="storeRoute"
        interface="employee"
        :program-id="programId"
      />
    </div>
    <template v-else>
      <div style="background-color: white; border-radius: 5px;">
        <p style="text-align: center; padding-top: 10px; padding-bottom: 10px">
          {{ trans('subscription.select_date_or_classroom') }}
        </p>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import { DatePicker, TimeSelect } from 'element-ui'
import _cloneDeep from 'lodash/cloneDeep'
import { defaultErrorMessage } from '@/helpers'
import { SINGLE } from '@/constants'
import EmployeeDayCalendarPerClassroom from '@/employees/components/Werkplekken/EmployeeDayCalendarPerClassroom.vue'

import locale from 'element-ui/lib/locale'
const lang = require(`element-ui/lib/locale/lang/${Lang.locale}`).default
locale.use(lang)


export default {
  name: 'EmployeeWerkplekkenCalendar',
  components: { EmployeeDayCalendarPerClassroom, DatePicker, TimeSelect },

  props: {
    apiRoute: {
      required: true,
      type: String,
    },
    filterRoute: {
      required: true,
      type: String,
    },
    storeRoute: {
      required: true,
      type: String,
    },
    programId: {
      required: true,
      type: String,
    }
  },

  data () {
    return {
      filter: {
        date: null,
        eventCourse: null,
        classroom: null,
        time: null,
      },

      showMoreFilterOptions: true,
      isLoading: true,
      classroomChanged: false,
      filterCourses: [],
      filterClassrooms: [],

      pickerOptions: {
        firstDayOfWeek: 1,
      },
    }
  },

  computed: {
    ...mapState({
      subscribables (state) {
        const subscribables = Object.values(state.subscribables.subscribables)
        if (this.filter.classroom != null) {
          return subscribables.sort((a, b) => {
            const eventA = state.events.events[a.event]
            const eventB = state.events.events[b.event]
            return moment(eventA.starttime).unix() > moment(eventB.starttime).unix() ? 1 : -1
          })
        }
        return subscribables
      },
    }),

    classroomIds () {
      return this.filterClassrooms.map(classroom => classroom.id)
    },

    courseFilterList () {
      return [
        {
          name: Lang.get('core.all') + ' ' + Lang.choice('core.courses', 2).toLowerCase(),
          id: null,
        },

        ...Object.values(this.filterCourses).map(course => ({ name: course.title, id: course.id })),
      ]
    },

    classroomFilterList () {
      return [
        {
          name: Lang.get('core.all') + ' ' + Lang.choice('core.classrooms', 2).toLowerCase(),
          id: null,
        },

        ...Object.values(this.filterClassrooms).map(classroom => ({ name: classroom.name, id: classroom.id })),
      ]
    },
  },

  watch: {
    filter: {
      deep: true,
      handler (newValue, oldValue) {
        if (newValue.classroom !== oldValue.classroom) {
          this.classroomChanged = true
        }

        if (!this.isLoading) {
          this.loadSubscribables()
        }
      },
    },
  },

  async created () {
    const today = new Date()
    this.filter.date = today.toISOString().slice(0, 10)

    await this.loadFilters()
    await this.loadSubscribables()
  },

  methods: {
    loadFilters () {
      const filter = {
        type: SINGLE,
      }

      return this.$store.dispatch('getFilterOptions', {
        apiRoute: this.filterRoute,
        queryParams: {
          filter: filter,
          includeClassrooms: true,
        },
      }).then((response) => {
        this.filterCourses = response.data.courses
        this.filterClassrooms = response.data.classrooms
      })
    },

    async loadSubscribables () {
      // if there is no classroom or date selected, don't make the call since we only show a message
      if (this.filter.date === null && this.filter.classroom === null) {
        return
      }

      this.isLoading = true
      const calls = []

      const filter = {
        type: SINGLE,
      }

      if (this.filter.eventCourse != null) {
        filter.eventCourses = this.filter.eventCourse
      }

      if (this.filter.classroom != null) {
        filter.classroom = this.filter.classroom
        // We only want future subscribables here
        filter.eventStart = moment().startOf('day').unix()
      } else {
        delete filter.eventStart
      }

      if (this.filter.date != null && !this.classroomChanged) {
        filter.date = this.filter.date
      }

      if (this.filter.time != null && !this.classroomChanged) {
        filter.time = this.filter.time
      }

      await this.$store.dispatch('resetSubscribableState')

      // if no classroom is selected we need to chunk the calls to avoid memory
      if (this.filter.classroom === null) {
        const chunkedClassroomIds = _.chunk(this.classroomIds, 15)

        for (const chunk of chunkedClassroomIds) {
          const newFilter = _cloneDeep(filter)
          newFilter.classroom = chunk
          calls.push(this.$store.dispatch('getSubscribables', {
            apiRoute: this.apiRoute,
            queryParams: {
              filter: newFilter,
              include: [
                'event.classrooms',
                'event.course',
                'subscriptions.student'
              ],
            },
          }))
        }

        await Promise.all(calls)
          .catch(error => defaultErrorMessage(error))
          .finally(() => {
            this.isLoading = false
            this.classroomChanged = false
          })
      } else {
        await this.$store.dispatch('getSubscribables', {
          apiRoute: this.apiRoute,
          queryParams: {
            filter: filter,
            include: [
              'event.classrooms',
              'event.course',
            ],
          },
        }).catch(error => defaultErrorMessage(error))
          .finally(() => {
            this.isLoading = false
            this.classroomChanged = false
          })
      }
    },
  },
}
</script>

<style scoped lang="scss">
.subscribables-per-day {
  background-color: #edf0f7;
  display: flex;
  flex-direction: column;
  height: inherit;
}

.filter-bar {

  .more-options {
    cursor: pointer;
  }

  display: flex;
  padding: 0px 10px 0px 10px;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  margin-top: 5px;
  margin-bottom: 0px;
  gap: 10px;

  &__option {
    flex: 1;
    min-width: 200px;
    width: 100%;
  }
}

.el-date-editor.el-input {
  width: inherit !important;
  border: none !important;
}
</style>
